import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext';
import { ProductProvider, useProduct } from './contexts/ProductContext';
import Sessions from './components/sessions/Sessions';
import SessionRunner from './components/sessions/SessionRunner';
import CreateSessionPage from './components/sessions/CreateSessionPage';
import FeaturesManagement from './components/features/FeaturesManagement';
import FeatureDetail from './components/features/FeatureDetail';
import Library from './components/features/v2/Library';
import EditTestCasePage from './components/features/EditTestCasePage';
import SettingsPanel from './components/settings/SettingsPanel';
import Insights from './components/insights/Insights';
import TestPlanManagement from './components/testplan/TestPlanManagement';
import TestPlanDetailPage from './components/testplan/TestPlanDetailPage';
import Login from './components/login/Login';
import Portal from './components/portal/Portal';
import Logo from './components/Logo';
import './App.css';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactElement; requireWorkspace?: boolean }> = ({ children, requireWorkspace = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const { currentWorkspace, loading: workspaceLoading } = useWorkspace();
  const location = useLocation();
  
  // Always wait for both auth and workspace to finish loading
  if (loading || workspaceLoading) {
    return <div style={{ textAlign: 'center', marginTop: '5rem' }}>Loading...</div>;
  }
  
  // Portal admins don't need workspace - check this first
  if (user?.isPortalAdmin) {
    // Still need to be authenticated
    if (!isAuthenticated) {
      return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    }
    return children;
  }
  
  // For workspace routes: Check authentication first, then workspace
  if (requireWorkspace) {
    // Must be authenticated first
    if (!isAuthenticated) {
      return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    }
    
    // If authenticated but no workspace, redirect to login
    // (Login page will handle workspace selection or show error)
    if (!currentWorkspace) {
      return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    }
  } else {
    // Non-workspace routes (like portal) - just check authentication
    if (!isAuthenticated) {
      return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    }
  }
  
  return children;
};

// Navbar Component
const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const { selectedProduct, products, setSelectedProduct, loading: productLoading } = useProduct();
  const location = useLocation();
  const isPortalAdmin = user?.isPortalAdmin;
  
  // Hide navbar on login page or when workspace selection is needed
  if (location.pathname === '/login' || (!isPortalAdmin && !currentWorkspace && user)) {
    return null;
  }
  
  return (
    <nav className="navbar">
      <div className="nav-container">
        <Link to={isPortalAdmin ? "/portal" : "/"} className="nav-logo">
          <Logo size={36} />
          <span className="nav-logo-text">ExoTest</span>
        </Link>
        <div className="nav-links">
          {isPortalAdmin ? (
            <NavLink to="/portal" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
              Portal Management
            </NavLink>
          ) : (
            <>
              <NavLink to="/" end className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                Sessions
              </NavLink>
              <NavLink to="/testplan" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                Test Plan
              </NavLink>
              <NavLink to="/library" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                Library
              </NavLink>
              <NavLink to="/insights" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                Insights
              </NavLink>
              <NavLink to="/admin" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                Settings
              </NavLink>
            </>
          )}
          {user && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {!isPortalAdmin && !productLoading && products.length > 0 && (
                <select
                  value={selectedProduct?.id || ''}
                  onChange={(e) => {
                    const product = products.find(p => p.id === e.target.value);
                    setSelectedProduct(product || null);
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    paddingRight: '2.5rem',
                    background: 'transparent',
                    color: '#7dd3fc',
                    border: '1px solid #7dd3fc',
                    borderRadius: '2px',
                    cursor: 'pointer',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    appearance: 'none',
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%237dd3fc' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E\")",
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 1rem center',
                  }}
                >
                  {products.map(product => (
                    <option key={product.id} value={product.id}>{product.name}</option>
                  ))}
                </select>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
                {!isPortalAdmin && currentWorkspace && (
                  <span style={{ color: '#7dd3fc', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {currentWorkspace.name}
                  </span>
                )}
                <span style={{ color: '#7dd3fc', fontSize: '0.875rem', fontWeight: 500 }}>{user.email}</span>
              </div>
              <button
                onClick={logout}
                style={{
                  padding: '0.5rem 1.25rem',
                  background: 'transparent',
                  color: '#ff3366',
                  border: '1px solid #ff3366',
                  borderRadius: '2px',
                  cursor: 'pointer',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#ff3366';
                  e.currentTarget.style.color = '#0a0a0f';
                  e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 51, 102, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#ff3366';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

// Footer Component
const Footer: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const isPortalAdmin = user?.isPortalAdmin;
  
  // Hide footer on login page or when workspace selection is needed
  if (location.pathname === '/login' || (!isPortalAdmin && !currentWorkspace && user)) {
    return null;
  }
  
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="app-footer">
      <div className="footer-content">
        <p>© {currentYear} Joshua Lawley. All rights reserved.</p>
        <p className="footer-product">ExoTest - Exploratory Testing Session Tracker</p>
      </div>
    </footer>
  );
};

function AppContent() {
  return (
    <Router>
      <div className="App">
        <Navbar />
        <main className="main-content">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/portal" element={
              <ProtectedRoute>
                <Portal />
              </ProtectedRoute>
            } />
            <Route path="/" element={
              <ProtectedRoute requireWorkspace={true}>
                <Sessions />
              </ProtectedRoute>
            } />
            <Route path="/sessions" element={
              <ProtectedRoute requireWorkspace={true}>
                <Sessions />
              </ProtectedRoute>
            } />
            <Route path="/sessions/new" element={
              <ProtectedRoute requireWorkspace={true}>
                <CreateSessionPage />
              </ProtectedRoute>
            } />
            <Route path="/session/:id" element={
              <ProtectedRoute requireWorkspace={true}>
                <SessionRunner />
              </ProtectedRoute>
            } />
            <Route path="/features" element={
              <ProtectedRoute requireWorkspace={true}>
                <FeaturesManagement />
              </ProtectedRoute>
            } />
            <Route path="/library" element={
              <ProtectedRoute requireWorkspace={true}>
                <Library />
              </ProtectedRoute>
            } />
            <Route path="/library/testcase/new" element={
              <ProtectedRoute requireWorkspace={true}>
                <EditTestCasePage />
              </ProtectedRoute>
            } />
            <Route path="/library/testcase/:testCaseId/edit" element={
              <ProtectedRoute requireWorkspace={true}>
                <EditTestCasePage />
              </ProtectedRoute>
            } />
            <Route path="/feature/:id" element={
              <ProtectedRoute requireWorkspace={true}>
                <FeatureDetail />
              </ProtectedRoute>
            } />
            <Route path="/testplan" element={
              <ProtectedRoute requireWorkspace={true}>
                <TestPlanManagement />
              </ProtectedRoute>
            } />
            <Route path="/testplan/:id" element={
              <ProtectedRoute requireWorkspace={true}>
                <TestPlanDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/insights" element={
              <ProtectedRoute requireWorkspace={true}>
                <Insights />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute requireWorkspace={true}>
                <SettingsPanel />
              </ProtectedRoute>
            } />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <WorkspaceProvider>
        <ProductProvider>
          <AppContent />
        </ProductProvider>
      </WorkspaceProvider>
    </AuthProvider>
  );
}

export default App;

